import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';

import { FETCH_TYPES } from 'helpers/constants';
import usePopulateMapData from 'hooks/usePopulateMapData';
import ChecklistTree from 'components/ChecklistTree';
import {
  getSelectedCropZones,
  mapPropertiesToTreeData
} from 'screens/Reports/helpers/helperFunctions';
import { ReportsContext } from 'screens/Reports/context/ReportsProvider';
import mapPropertiesToCropGroupedTreeData from '../../helpers/mapPropertiesToCropGroupedTreeData';

const groupByOptions = [
  { value: 'crop', label: 'Group by crop' },
  { value: 'farm', label: 'Group by farm' }
];

const PropertiesSection = () => {
  const { values, setFieldValue } = useFormikContext();
  const {
    state: { formSelections, propertiesGroupBy },
    setPropertiesGroupBy
  } = useContext(ReportsContext);

  const [checked, setChecked] = useState([]);

  const seasonIds = useMemo(
    () => values.selectedCropSeasons?.map(cropSeason => cropSeason?.id) ?? [],
    [values.selectedCropSeasons]
  );

  const { dataSorted, loading, reloadData } = usePopulateMapData({
    fetchType: FETCH_TYPES.cropSeasonsMap
  });

  useEffect(() => {
    if (seasonIds.length) {
      reloadData({ seasonIds });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonIds]);

  const handleGroupingChange = groupBy => {
    // reset checked items when changing grouping to avoid state issues
    setChecked([]);

    const newGroupBy = groupBy === 'farm' ? 'property' : 'crop';
    setPropertiesGroupBy(newGroupBy);
    setFieldValue('propertiesGroupBy', newGroupBy);
  };

  // Map API response to tree data
  const treeData = useMemo(() => {
    if (loading || !dataSorted?.properties?.length) return [];

    if (formSelections.properties.length && !checked.length) {
      setChecked(formSelections.properties);
    }

    // mapping function based on grouping selection
    if (propertiesGroupBy === 'crop') {
      return mapPropertiesToCropGroupedTreeData(dataSorted.properties);
    }
    return mapPropertiesToTreeData(dataSorted.properties);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSorted, loading, propertiesGroupBy]);

  // Update form values with checked items
  useEffect(() => {
    if (!dataSorted?.properties?.length || !seasonIds?.length) return;

    const selectedCropZones = getSelectedCropZones(
      dataSorted.properties,
      checked
    );

    setFieldValue('checkedProperties', checked);
    setFieldValue('selectedCropzones', selectedCropZones);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  return (
    <div className="flex flex-col items-center bg-white">
      {seasonIds.length > 0 && (
        <div className="w-full">
          <h1
            className="font-body font-semibold text-xl"
            style={{ color: '#14151C' }}
          >
            Properties
          </h1>
          <ChecklistTree
            key={`checklist-tree-${propertiesGroupBy}`}
            treeData={treeData}
            loading={loading}
            checked={checked}
            setChecked={setChecked}
            initialSelectAll={!formSelections.properties.length}
            groupBy={propertiesGroupBy === 'property' ? 'farm' : 'crop'}
            onGroupByChange={handleGroupingChange}
            groupByOptions={groupByOptions}
          />
        </div>
      )}
    </div>
  );
};

export default PropertiesSection;
